/**
 * useTheme — Battle Carp light/dark theme provider.
 *
 * State:
 *   - `theme`: 'light' | 'dark', the active palette.
 *   - `setTheme(next)`: switches and persists the choice.
 *
 * Persistence:
 *   - Native: SecureStore (async). Web: localStorage (synchronous) — read in
 *     the initial state so the saved theme is applied on the very first render
 *     with no fetch/flicker. (SecureStore is a no-op on web, which is why the
 *     choice never used to stick there.)
 *   - Falls back to the system colour scheme when nothing is saved.
 *
 * Mechanics:
 *   - On web: toggle the `dark` class on `document.documentElement`, which
 *     swaps the CSS variables defined in apps/mobile/global.css.
 *   - On native: ride NativeWind's color-scheme context via
 *     `useColorScheme().setColorScheme(...)` — it does the equivalent of
 *     the class swap for native render trees.
 *
 * Consumers:
 *   - Components that need to branch on theme (e.g. logo variant) call
 *     `useTheme()` directly. Pure styling should prefer the semantic
 *     Tailwind tokens (`bg-paper`, `text-ink`, etc.) which swap on their
 *     own — only branch via this hook for non-CSS things (SVG color
 *     props, image variants).
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
} from 'react'
import { Platform, Appearance } from 'react-native'
import { useColorScheme } from 'nativewind'
import * as SecureStore from 'expo-secure-store'
import { addBreadcrumb } from '../lib/reporting'

const STORAGE_KEY = 'warcarp.theme'

export type ThemeMode = 'light' | 'dark'

interface ThemeContextValue {
  theme: ThemeMode
  setTheme: (mode: ThemeMode) => void
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function applyTheme(mode: ThemeMode) {
  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    const root = document.documentElement
    if (mode === 'dark') root.classList.add('dark')
    else root.classList.remove('dark')
  }
}

/** Synchronously read the saved theme on web (localStorage). Returns null on
 * native (SecureStore is async — loaded in an effect) or when nothing valid is
 * stored. Reading sync lets web apply the right theme on the first render. */
function readSavedThemeSync(): ThemeMode | null {
  if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
    try {
      const v = localStorage.getItem(STORAGE_KEY)
      return v === 'light' || v === 'dark' ? v : null
    } catch {
      return null
    }
  }
  return null
}

/** Persist the choice: localStorage on web, SecureStore on native. */
function persistTheme(mode: ThemeMode) {
  if (Platform.OS === 'web') {
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, mode)
    } catch {
      // private mode / storage disabled — best effort.
    }
  } else {
    void SecureStore.setItemAsync(STORAGE_KEY, mode).catch((err: unknown) => {
      // SecureStore write failure is non-fatal — theme resets to system
      // default on next launch — but repeated failures hint at a device
      // keychain issue worth tracking in Sentry breadcrumbs.
      addBreadcrumb({
        category: 'theme',
        message: 'persist theme failed',
        level: 'warning',
        data: { mode, err: err instanceof Error ? err.message : String(err) },
      })
    })
  }
}

// Apply the DOM class BEFORE paint on web to avoid a flash; a plain effect is
// fine on native.
const useThemeApplyEffect =
  Platform.OS === 'web' && typeof document !== 'undefined' ? useLayoutEffect : useEffect

/** Read the system colour-scheme synchronously. Web: matchMedia.
 * Native: Appearance.getColorScheme(). Returns null when the OS has
 * not declared one, in which case we fall back to dark. */
function getSystemTheme(): ThemeMode {
  if (Platform.OS === 'web' && typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  const sys = Appearance.getColorScheme()
  return sys === 'light' ? 'light' : 'dark'
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Web reads the saved theme synchronously (no flicker); native falls back to
  // the SYSTEM scheme here and overrides from SecureStore in the effect below.
  const [theme, setThemeState] = useState<ThemeMode>(() => readSavedThemeSync() ?? getSystemTheme())
  const { setColorScheme } = useColorScheme()

  // Native: load the saved preference (SecureStore is async). Web already read
  // it synchronously above, so skip.
  useEffect(() => {
    if (Platform.OS === 'web') return
    let cancelled = false
    void (async () => {
      try {
        const saved = await SecureStore.getItemAsync(STORAGE_KEY)
        if (cancelled) return
        if (saved === 'light' || saved === 'dark') {
          setThemeState(saved)
        }
      } catch {
        // fall through to the default.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Apply whenever the mode changes (pre-paint on web — see useThemeApplyEffect).
  useThemeApplyEffect(() => {
    applyTheme(theme)
    setColorScheme(theme)
  }, [theme, setColorScheme])

  const setTheme = useCallback((mode: ThemeMode) => {
    setThemeState(mode)
    persistTheme(mode)
  }, [])

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next: ThemeMode = prev === 'dark' ? 'light' : 'dark'
      persistTheme(next)
      return next
    })
  }, [])

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}
